<template>
    <img
        v-if="part === 'image' && activeCta"
        :src="activeCta.image"
        :alt="displayTitle"
        class="h-[176px] w-[176px] aspect-square rounded-[6px] object-cover sm:h-[208px] sm:w-[208px]"
    />

    <div
        v-else-if="part === 'mobile' && activeCta"
        class="flex w-full min-w-0 items-start gap-3"
        aria-live="polite">
        <img
            :src="activeCta.image"
            :alt="displayTitle"
            class="h-20 w-20 shrink-0 rounded-[6px] object-cover" />

        <div class="flex min-w-0 flex-1 items-start gap-2">
            <div class="min-w-0 max-w-[65%]">
                <h2
                    class="line-clamp-2 break-words whitespace-pre-line text-base font-bold leading-snug text-player-contrast"
                    :title="activeCta.title">
                    {{ displayTitle }}
                </h2>
            </div>

            <a
                :href="activeCta.url"
                :title="linkTitle"
                :aria-label="linkAriaLabel"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex min-w-[6.5rem] shrink-0 items-center justify-center rounded-md bg-player-accent px-6 py-2.5 text-center text-sm font-medium leading-tight text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-player-accent focus:ring-offset-2 focus:ring-offset-player-background"
                @click="emit('clicked', activeCta?.id)">
                {{ activeCta.button }}
            </a>
        </div>
    </div>

    <div
        v-else-if="part === 'header' && activeCta"
        class="flex w-full min-w-0 items-start gap-3 sm:gap-4"
        aria-live="polite">
        <div class="min-w-0 max-w-[70%]">
            <h2
                class="line-clamp-2 break-words whitespace-pre-line text-xl font-bold leading-snug text-player-contrast"
                :title="activeCta.title">
                {{ displayTitle }}
            </h2>
        </div>

        <a
            :href="activeCta.url"
            :title="linkTitle"
            :aria-label="linkAriaLabel"
            target="_blank"
            rel="noopener noreferrer"
            class="ml-auto inline-flex min-w-[8rem] shrink-0 items-center justify-center rounded-md bg-player-accent px-7 py-3 text-center text-base font-medium leading-tight text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-player-accent focus:ring-offset-2 focus:ring-offset-player-background"
            @click="emit('clicked', activeCta?.id)">
            {{ activeCta.button }}
        </a>
    </div>
</template>

<script setup>
import { computed } from "vue";
import { formatCtaTitle, resolveActiveCta } from "../helpers.js";

const props = defineProps({
    ctas: {
        type: Array,
        required: true,
    },
    seek: {
        type: Number,
        required: true,
    },
    part: {
        type: String,
        required: true,
        validator: (value) => ["image", "header", "mobile"].includes(value),
    },
});

const emit = defineEmits(["clicked"]);

const activeCta = computed(() => resolveActiveCta(props.seek, props.ctas));

const displayTitle = computed(() => formatCtaTitle(activeCta.value?.title ?? ""));

const linkTitle = computed(() => {
    if (!activeCta.value) {
        return "";
    }

    return `${activeCta.value.title} — ${activeCta.value.button}`;
});

const linkAriaLabel = computed(() => linkTitle.value);
</script>
